import { Cookie, Mail } from "lucide-react";

const SUPPORT_EMAIL = "support@scudononni.it";

export default function CookiePolicy() {
  return (
    <div data-testid="cookie-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      <div className="text-center mb-12 animate-fade-up">
        <Cookie size={56} className="mx-auto text-[#1E3A8A] mb-5" />
        <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-[#1E3A8A] leading-tight">
          Informativa sui Cookie
        </h1>
        <p className="mt-4 text-lg text-gray-600">
          In conformità al Provvedimento del Garante n. 231/2021 e alla direttiva ePrivacy
        </p>
        <p className="mt-2 text-base text-gray-500">Ultimo aggiornamento: 27 febbraio 2026</p>
      </div>

      <div className="bg-white rounded-[2rem] shadow-lg border-4 border-blue-50 p-6 md:p-12 space-y-8 text-lg md:text-xl text-gray-700 leading-relaxed">
        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-3">Cosa sono i cookie</h2>
          <p>
            I cookie sono piccoli file di testo che i siti visitati inviano al tuo dispositivo,
            dove vengono memorizzati per essere ritrasmessi al sito stesso alla visita successiva.
            Insieme ai cookie, alcuni siti utilizzano anche tecnologie simili come il <em>localStorage</em>
            del browser.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-3">Quali cookie usiamo</h2>
          <p className="mb-4">
            ScudoNonni utilizza <strong>cookie e tecnologie tecniche necessarie</strong> al
            corretto funzionamento del sito e, <strong>solo previo tuo consenso</strong>, cookie
            <strong> statistici anonimi</strong> di Google Analytics per misurare quante persone
            usano il servizio. <strong>Non utilizziamo cookie di profilazione, di marketing o di
            tracciamento pubblicitario</strong> di terze parti.
          </p>

          <h3 className="font-heading text-xl font-bold text-[#1E3A8A] mt-6 mb-3">
            Cookie tecnici necessari (sempre attivi)
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-base md:text-lg">
              <thead>
                <tr className="bg-[#DBEAFE] text-[#1E3A8A]">
                  <th className="border-2 border-blue-200 p-3 text-left">Nome</th>
                  <th className="border-2 border-blue-200 p-3 text-left">Tipo</th>
                  <th className="border-2 border-blue-200 p-3 text-left">Finalità</th>
                  <th className="border-2 border-blue-200 p-3 text-left">Durata</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border-2 border-blue-200 p-3"><code>scudononni_client_id</code></td>
                  <td className="border-2 border-blue-200 p-3">Tecnico (localStorage)</td>
                  <td className="border-2 border-blue-200 p-3">Identificativo anonimo per contare le 3 verifiche giornaliere</td>
                  <td className="border-2 border-blue-200 p-3">Persistente fino a cancellazione manuale</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border-2 border-blue-200 p-3"><code>scudononni_cookie_consent</code></td>
                  <td className="border-2 border-blue-200 p-3">Tecnico (localStorage)</td>
                  <td className="border-2 border-blue-200 p-3">Memorizza la tua scelta sul banner cookie</td>
                  <td className="border-2 border-blue-200 p-3">12 mesi</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="font-heading text-xl font-bold text-[#1E3A8A] mt-6 mb-3">
            Cookie statistici (solo con il tuo consenso)
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-base md:text-lg">
              <thead>
                <tr className="bg-[#DCFCE7] text-[#14532D]">
                  <th className="border-2 border-green-200 p-3 text-left">Nome</th>
                  <th className="border-2 border-green-200 p-3 text-left">Tipo</th>
                  <th className="border-2 border-green-200 p-3 text-left">Finalità</th>
                  <th className="border-2 border-green-200 p-3 text-left">Durata</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border-2 border-green-200 p-3"><code>_ga</code></td>
                  <td className="border-2 border-green-200 p-3">Statistico (Google Analytics 4)</td>
                  <td className="border-2 border-green-200 p-3">Distingue gli utenti in modo anonimo (IP anonimizzato)</td>
                  <td className="border-2 border-green-200 p-3">2 anni</td>
                </tr>
                <tr className="bg-green-50/40">
                  <td className="border-2 border-green-200 p-3"><code>_ga_MWLMVPHK79</code></td>
                  <td className="border-2 border-green-200 p-3">Statistico (Google Analytics 4)</td>
                  <td className="border-2 border-green-200 p-3">Tiene traccia della sessione su questa specifica proprietà GA4</td>
                  <td className="border-2 border-green-200 p-3">2 anni</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-base mt-3 text-gray-600">
            I cookie statistici si attivano <strong>solo se clicchi "Accetta tutti"</strong> nel banner.
            Se scegli "Solo necessari", Google Analytics non viene caricato. Puoi cambiare idea quando vuoi
            usando il link <strong>"Modifica preferenze cookie"</strong> nel footer.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-3">Cookie di terze parti</h2>
          <p>
            Gli unici cookie di terze parti che possono essere installati sono quelli di{" "}
            <strong>Google Analytics</strong> (sopra descritti), e solo previo tuo consenso esplicito.
            I dati inviati al fornitore di intelligenza artificiale (Google Gemini) per l'analisi dei messaggi
            sono trasmessi via API server-to-server e <strong>non comportano impianto di cookie</strong> sul tuo dispositivo.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-3">Come gestire i cookie</h2>
          <p className="mb-3">
            Puoi modificare le tue preferenze in qualsiasi momento:
          </p>
          <ul className="list-disc pl-8 space-y-2">
            <li>
              Clicca su <strong>"Modifica preferenze cookie"</strong> nel footer per riaprire il banner
              e cambiare la tua scelta.
            </li>
            <li>
              <strong>Cancellare i dati locali</strong> dal tuo browser (impostazioni → privacy → cancella dati di navigazione).
            </li>
            <li>
              <strong>Bloccare il localStorage</strong> per questo sito dalle impostazioni del browser.
              Tieni presente che disabilitandolo non sarà possibile contare le verifiche giornaliere
              e il servizio potrebbe non funzionare correttamente.
            </li>
          </ul>
          <p className="mt-3">
            Guide per i browser più comuni:{" "}
            <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-[#1E3A8A] underline">Chrome</a>,{" "}
            <a href="https://support.mozilla.org/it/kb/Gestione%20dei%20cookie" target="_blank" rel="noopener noreferrer" className="text-[#1E3A8A] underline">Firefox</a>,{" "}
            <a href="https://support.apple.com/it-it/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-[#1E3A8A] underline">Safari</a>,{" "}
            <a href="https://support.microsoft.com/it-it/microsoft-edge" target="_blank" rel="noopener noreferrer" className="text-[#1E3A8A] underline">Edge</a>.
          </p>
        </section>

        <section className="bg-[#DBEAFE] rounded-2xl p-6">
          <h2 className="font-heading text-2xl font-bold text-[#1E3A8A] mb-3 flex items-center gap-3">
            <Mail size={28} /> Contatti
          </h2>
          <p>
            Per dubbi o richieste relative ai cookie:{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#1E3A8A] font-bold underline">
              {SUPPORT_EMAIL}
            </a>
          </p>
        </section>
      </div>
    </div>
  );
}
